import React from "react";
import styles from "../../styles/pages/static/TermsPage.module.css";
import PageSeo from "../../components/common/PageSeo";

const TermsPage = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const lastUpdated = "July 24, 2026";

  return (
    <div className="pageContainer">
      <PageSeo
        title="Terms & Conditions"
        description="Read Simpary's terms and conditions of use."
        pathname="/terms"
      />
      <div className="innerContainer">
        <div className={styles.termsPage}>
        {/* Header Section */}
        <section className={styles.headerSection}>
          <div className={styles.headerContent}>
            <h1 className={styles.pageTitle}>Terms & Conditions</h1>
            <p className={styles.lastUpdated}>Last Updated: {lastUpdated}</p>
          </div>
        </section>

        {/* Content Section */}
        <section className={styles.contentSection}>
          <div className={styles.container}>
            <div className={styles.contentGrid}>
              {/* Table of Contents */}
              <aside className={styles.tableOfContents}>
                <h3 className={styles.tocTitle}>Table of Contents</h3>
                <nav className={styles.tocNav}>
                  <a href="#acceptance" onClick={() => scrollToSection("acceptance")} className={styles.tocLink}>
                    1. Acceptance of Terms
                  </a>
                  <a href="#user-accounts" onClick={() => scrollToSection("user-accounts")} className={styles.tocLink}>
                    2. User Accounts
                  </a>
                  <a href="#platform-usage" onClick={() => scrollToSection("platform-usage")} className={styles.tocLink}>
                    3. Platform Usage
                  </a>
                  <a href="#content-guidelines" onClick={() => scrollToSection("content-guidelines")} className={styles.tocLink}>
                    4. Content Guidelines
                  </a>
                  <a href="#intellectual-property" onClick={() => scrollToSection("intellectual-property")} className={styles.tocLink}>
                    5. Intellectual Property
                  </a>
                  <a href="#product-information" onClick={() => scrollToSection("product-information")} className={styles.tocLink}>
                    6. Product Information
                  </a>
                  <a href="#user-content" onClick={() => scrollToSection("user-content")} className={styles.tocLink}>
                    7. User-Generated Content
                  </a>
                  <a href="#prohibited-activities" onClick={() => scrollToSection("prohibited-activities")} className={styles.tocLink}>
                    8. Prohibited Activities
                  </a>
                  <a href="#liability" onClick={() => scrollToSection("liability")} className={styles.tocLink}>
                    9. Limitation of Liability
                  </a>
                  <a href="#dispute-resolution" onClick={() => scrollToSection("dispute-resolution")} className={styles.tocLink}>
                    10. Dispute Resolution
                  </a>
                  <a href="#termination" onClick={() => scrollToSection("termination")} className={styles.tocLink}>
                    11. Termination
                  </a>
                  <a href="#changes" onClick={() => scrollToSection("changes")} className={styles.tocLink}>
                    12. Changes to Terms
                  </a>
                  <a href="#contact" onClick={() => scrollToSection("contact")} className={styles.tocLink}>
                    13. Contact Information
                  </a>
                </nav>
              </aside>

              {/* Main Content */}
              <div className={styles.mainContent}>
                {/* Acceptance of Terms */}
                <section id="acceptance" className={styles.section}>
                  <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
                  <p className={styles.paragraph}>
                    Welcome to Simpary. By accessing or using our electronics comparison platform, 
                    you agree to be bound by these Terms and Conditions ("Terms"). These Terms 
                    constitute a legally binding agreement between you and Simpary.
                  </p>
                  <p className={styles.paragraph}>
                    If you do not agree to these Terms, you must not access or use our services. 
                    Your continued use of our platform constitutes acceptance of any updates or 
                    modifications to these Terms.
                  </p>
                </section>

                {/* User Accounts */}
                <section id="user-accounts" className={styles.section}>
                  <h2 className={styles.sectionTitle}>2. User Accounts and Responsibilities</h2>
                  
                  <h3 className={styles.subsectionTitle}>2.1 Account Creation</h3>
                  <p className={styles.paragraph}>
                    To access certain features, you must create an account. You agree to:
                  </p>
                  <ul className={styles.list}>
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and update your information to keep it accurate</li>
                    <li>Maintain the security of your password and account</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized access</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>2.2 Account Eligibility</h3>
                  <p className={styles.paragraph}>
                    You must be at least 13 years old to create an account. If you are under 18, 
                    you must have parental or guardian consent to use our services.
                  </p>

                  <h3 className={styles.subsectionTitle}>2.3 Account Security</h3>
                  <p className={styles.paragraph}>
                    You are responsible for maintaining the confidentiality of your account credentials. 
                    We are not liable for any loss or damage arising from your failure to protect 
                    your account information.
                  </p>
                </section>

                {/* Platform Usage */}
                <section id="platform-usage" className={styles.section}>
                  <h2 className={styles.sectionTitle}>3. Platform Usage Rules</h2>
                  <p className={styles.paragraph}>
                    When using Simpary, you agree to:
                  </p>
                  <ul className={styles.list}>
                    <li>Use the platform only for lawful purposes</li>
                    <li>Comply with all applicable laws and regulations</li>
                    <li>Respect the rights and dignity of other users</li>
                    <li>Not interfere with the operation of the platform</li>
                    <li>Not attempt to gain unauthorized access to any systems</li>
                    <li>Not use automated tools to access or collect data without permission</li>
                  </ul>
                </section>

                {/* Content Guidelines */}
                <section id="content-guidelines" className={styles.section}>
                  <h2 className={styles.sectionTitle}>4. Content Submission Guidelines</h2>
                  
                  <h3 className={styles.subsectionTitle}>4.1 Reviews and Ratings</h3>
                  <p className={styles.paragraph}>
                    When submitting reviews or ratings, you must:
                  </p>
                  <ul className={styles.list}>
                    <li>Base your review on genuine experience with the product</li>
                    <li>Provide honest and accurate information</li>
                    <li>Avoid conflicts of interest (e.g., reviewing your own products)</li>
                    <li>Not receive compensation for positive or negative reviews</li>
                    <li>Use respectful and appropriate language</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>4.2 Community Interactions</h3>
                  <p className={styles.paragraph}>
                    When interacting with other users, you must:
                  </p>
                  <ul className={styles.list}>
                    <li>Be respectful and courteous</li>
                    <li>Avoid harassment, bullying, or threats</li>
                    <li>Not share personal information of others</li>
                    <li>Not engage in spam or excessive self-promotion</li>
                  </ul>
                </section>

                {/* Intellectual Property */}
                <section id="intellectual-property" className={styles.section}>
                  <h2 className={styles.sectionTitle}>5. Intellectual Property Rights</h2>
                  
                  <h3 className={styles.subsectionTitle}>5.1 Our Content</h3>
                  <p className={styles.paragraph}>
                    All content on Simpary, including but not limited to text, graphics, logos, 
                    images, software, and data compilations, is owned by Simpary or its licensors 
                    and is protected by copyright, trademark, and other intellectual property laws.
                  </p>

                  <h3 className={styles.subsectionTitle}>5.2 Limited License</h3>
                  <p className={styles.paragraph}>
                    We grant you a limited, non-exclusive, non-transferable license to access and 
                    use our platform for personal, non-commercial purposes. This license does not 
                    include the right to:
                  </p>
                  <ul className={styles.list}>
                    <li>Modify, copy, or distribute our content</li>
                    <li>Reverse engineer or decompile any software</li>
                    <li>Remove any copyright or proprietary notices</li>
                    <li>Use our content for commercial purposes without permission</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>5.3 Trademarks</h3>
                  <p className={styles.paragraph}>
                    "Simpary" and related marks are trademarks of Simpary. You may not use our 
                    trademarks without our prior written consent.
                  </p>
                </section>

                {/* Product Information */}
                <section id="product-information" className={styles.section}>
                  <h2 className={styles.sectionTitle}>6. Product Information Accuracy</h2>
                  <p className={styles.paragraph}>
                    We strive to provide accurate and up-to-date product information, including 
                    specifications, prices, and availability. However:
                  </p>
                  <ul className={styles.list}>
                    <li>Product information is provided by manufacturers and third parties</li>
                    <li>We do not guarantee the accuracy or completeness of information</li>
                    <li>Prices and availability may change without notice</li>
                    <li>We are not responsible for errors or omissions in product data</li>
                    <li>You should verify information directly with retailers or manufacturers</li>
                  </ul>
                  <p className={styles.paragraph}>
                    Simpary is a comparison and community platform. We do not sell products, 
                    process payments, or take possession of goods. We may earn commissions from 
                    affiliate links to third-party retailers, which helps us maintain our free services.
                  </p>
                  <p className={styles.paragraph}>
                    Our platform may also let users post listings, make offers, or message each 
                    other about items. Those interactions are solely between the users involved. 
                    Any sale, payment, shipping, or meetup happens off Simpary. We are not a party 
                    to those transactions and do not provide escrow, payment processing, buyer or 
                    seller protection, or dispute resolution for user-to-user deals.
                  </p>
                </section>

                {/* User-Generated Content */}
                <section id="user-content" className={styles.section}>
                  <h2 className={styles.sectionTitle}>7. User-Generated Content</h2>
                  
                  <h3 className={styles.subsectionTitle}>7.1 License to Simpary</h3>
                  <p className={styles.paragraph}>
                    By submitting content (reviews, comments, lists, etc.) to Simpary, you grant us 
                    a worldwide, non-exclusive, royalty-free, perpetual, and transferable license to:
                  </p>
                  <ul className={styles.list}>
                    <li>Use, reproduce, and distribute your content</li>
                    <li>Modify or adapt your content for display purposes</li>
                    <li>Create derivative works based on your content</li>
                    <li>Publicly display and perform your content</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>7.2 Content Ownership</h3>
                  <p className={styles.paragraph}>
                    You retain ownership of content you submit. However, by posting publicly, you 
                    acknowledge that others may view, share, and interact with your content.
                  </p>

                  <h3 className={styles.subsectionTitle}>7.3 Content Moderation</h3>
                  <p className={styles.paragraph}>
                    We reserve the right to review, modify, or remove any user-generated content 
                    that violates these Terms, our content guidelines, or applicable laws.
                  </p>
                </section>

                {/* Prohibited Activities */}
                <section id="prohibited-activities" className={styles.section}>
                  <h2 className={styles.sectionTitle}>8. Prohibited Activities</h2>
                  <p className={styles.paragraph}>
                    You are prohibited from:
                  </p>
                  <ul className={styles.list}>
                    <li>Violating any laws or regulations</li>
                    <li>Infringing on intellectual property rights</li>
                    <li>Submitting false, misleading, or fraudulent information</li>
                    <li>Impersonating others or misrepresenting affiliations</li>
                    <li>Distributing viruses, malware, or harmful code</li>
                    <li>Attempting to hack, disrupt, or damage our systems</li>
                    <li>Scraping or data mining without authorization</li>
                    <li>Creating multiple accounts to manipulate ratings or reviews</li>
                    <li>Engaging in any form of harassment or abuse</li>
                    <li>Using the platform for commercial purposes without permission</li>
                  </ul>
                </section>

                {/* Limitation of Liability */}
                <section id="liability" className={styles.section}>
                  <h2 className={styles.sectionTitle}>9. Limitation of Liability</h2>
                  
                  <h3 className={styles.subsectionTitle}>9.1 Service Disclaimer</h3>
                  <p className={styles.paragraph}>
                    Our platform is provided "as is" and "as available" without warranties of any 
                    kind, either express or implied. We do not warrant that:
                  </p>
                  <ul className={styles.list}>
                    <li>The service will be uninterrupted or error-free</li>
                    <li>Defects will be corrected</li>
                    <li>The service is free from viruses or harmful components</li>
                    <li>Information provided is accurate or reliable</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>9.2 Limitation of Damages</h3>
                  <p className={styles.paragraph}>
                    To the fullest extent permitted by law, Simpary shall not be liable for any 
                    indirect, incidental, special, consequential, or punitive damages, including 
                    loss of profits, data, or other intangible losses resulting from:
                  </p>
                  <ul className={styles.list}>
                    <li>Your use or inability to use the service</li>
                    <li>Unauthorized access to your data or communications</li>
                    <li>Conduct of third parties on the platform</li>
                    <li>Any other matter relating to the service</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>9.3 Third-Party and User Listings</h3>
                  <p className={styles.paragraph}>
                    We are not responsible for the quality, safety, legality, or accuracy of 
                    products or listings on our platform — whether from catalog data, affiliate 
                    retailers, or other users. Purchases through third-party retailers and 
                    user-to-user deals are between you and those parties. Simpary is not a party 
                    to those transactions and is not liable for payment disputes, non-delivery, 
                    misrepresentation, or other issues arising from them.
                  </p>
                </section>

                {/* Dispute Resolution */}
                <section id="dispute-resolution" className={styles.section}>
                  <h2 className={styles.sectionTitle}>10. Dispute Resolution</h2>
                  
                  <h3 className={styles.subsectionTitle}>10.1 Informal Resolution</h3>
                  <p className={styles.paragraph}>
                    If you have a dispute with us, you agree to first contact us and attempt to 
                    resolve the dispute informally.
                  </p>

                  <h3 className={styles.subsectionTitle}>10.2 Governing Law</h3>
                  <p className={styles.paragraph}>
                    These Terms shall be governed by and construed in accordance with applicable 
                    laws, without regard to conflict of law principles.
                  </p>

                  <h3 className={styles.subsectionTitle}>10.3 Arbitration</h3>
                  <p className={styles.paragraph}>
                    Any disputes that cannot be resolved informally shall be resolved through 
                    binding arbitration, except where prohibited by law. You waive your right to 
                    participate in class action lawsuits.
                  </p>
                </section>

                {/* Termination */}
                <section id="termination" className={styles.section}>
                  <h2 className={styles.sectionTitle}>11. Termination</h2>
                  
                  <h3 className={styles.subsectionTitle}>11.1 By You</h3>
                  <p className={styles.paragraph}>
                    You may terminate your account at any time by contacting us or using the 
                    account deletion feature in your settings.
                  </p>

                  <h3 className={styles.subsectionTitle}>11.2 By Us</h3>
                  <p className={styles.paragraph}>
                    We reserve the right to suspend or terminate your account if you:
                  </p>
                  <ul className={styles.list}>
                    <li>Violate these Terms or our policies</li>
                    <li>Engage in fraudulent or illegal activities</li>
                    <li>Pose a security risk to the platform or other users</li>
                    <li>Provide false information</li>
                  </ul>

                  <h3 className={styles.subsectionTitle}>11.3 Effect of Termination</h3>
                  <p className={styles.paragraph}>
                    Upon termination, your right to use the platform will cease immediately. 
                    Provisions that should reasonably survive termination will remain in effect.
                  </p>
                </section>

                {/* Changes to Terms */}
                <section id="changes" className={styles.section}>
                  <h2 className={styles.sectionTitle}>12. Changes to Terms</h2>
                  <p className={styles.paragraph}>
                    We may update these Terms from time to time. We will notify you of material 
                    changes by:
                  </p>
                  <ul className={styles.list}>
                    <li>Posting the updated Terms on our platform</li>
                    <li>Updating the "Last Updated" date</li>
                    <li>Sending email notifications for significant changes</li>
                  </ul>
                  <p className={styles.paragraph}>
                    Your continued use of our services after changes become effective constitutes 
                    acceptance of the updated Terms. If you do not agree to the changes, you must 
                    stop using our services.
                  </p>
                </section>

                {/* Contact */}
                <section id="contact" className={styles.section}>
                  <h2 className={styles.sectionTitle}>13. Contact Information</h2>
                  <p className={styles.paragraph}>
                    If you have questions or concerns about these Terms, please contact us:
                  </p>
                  <div className={styles.contactInfo}>
                    <p><strong>Email:</strong> legal@simpary.com</p>
                    <p><strong>Contact Form:</strong> <a href="/contact">simpary.com/contact</a></p>
                  </div>
                  <p className={styles.paragraph}>
                    We will respond to your inquiry as soon as possible, typically within 30 days.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
